import type React from 'react';
import { useEffect, useState } from 'react';
import { api } from 'src/services/api';
import AutocompleteBank from 'src/components/AutoCompleteBank/AutoCompleteBank';
import { toast } from 'react-toastify';
import { Button } from '../Button';
import { Truck } from 'lucide-react';
import { GradeSize } from 'src/model/GradeSize';
import { Grade } from 'src/model/Grade';
import Client from 'src/model/User';
import dayjs from 'dayjs';
import { Product } from 'src/model/Product';
import identifyApi from 'src/utils/localApi';

interface ProductOrderItem {
  quantity: number;
  id: string;
  unitValue: number;
  totalValue: number;
  product: null;
  itemName: string;
  productDTO: Product;
  statusType: string;
  cancelDate: Date;
  syncDate: string | null;
  grade: Grade;
  gradeSize: GradeSize;
  trackingCode: string | null;
}

interface ClientDetails {
  id: string;
  username: string;
  address: {
    id: string;
    zipcode: string;
    country: string;
    state: string;
    city: string;
    district: string;
    street: string;
    complement: string;
    number: string;
    main: boolean | null;
  };
  email: string;
  cellPhone: string;
}

interface OrderSummary {
  multiSellers: boolean;
  id: string;
  pagarmeID: string;
  code: string;
  clientId: string;
  client: string;
  orderDate: Date;
  locator: string | null;
  grade: Grade;
  paymentStatus: string;
  discountCoupon: string | null;
  amount: number;
  orderStatus: string;
  totalValue: number;
  paidAt: string | null;
  paymentInfo: string;
  sendValue: number;
  receivePrevision: string;
  cancelReason: string | null;
  cancelRequestDate: string | null;
  cancelDate: string | null;
  cancelUser: string | null;
  reverseValue: number;
  sendEmail: boolean;
  sendPush: boolean;
  correiosService: string;
  orderItems: ProductOrderItem[];
  paymentMethod: string;
  pagarmeStatus?: 'paid' | 'pending' | 'checked';
  voucherOrderId?: string;
  voucherStatus?: string;
}

interface VoucherOrderSummary {
  orderId: string;
  orderCode: string;
  voucherStatus: string;
  paymentStatus: string;
  orderStatus: string;
}

interface OrderDetails {
  orderItens: ProductOrderItem[];
  client: ClientDetails;
  status: string;
  orderDate: string;
  totalValue: number;
}

interface PrepostItem {
  id: string;
  remetente: {
    nome: string;
    cpfCnpj: string;
    endereco: {
      id: number;
      cep: string;
      logradouro: string;
      numero: string;
      complemento: string;
      bairro: string;
      cidade: string;
      uf: string;
    };
  };
  destinatario: {
    nome: string;
    cpfCnpj: string;
    endereco: {
      id: number;
      cep: string;
      logradouro: string;
      numero: string;
      complemento: string;
      bairro: string;
      cidade: string;
      uf: string;
    };
  };
  codigoObjeto: string;
  codigoServico: string;
  servico: string;
  itensDeclaracaoConteudo: Array<{
    conteudo: string;
    quantidade: number;
    valor: number;
  }>;
  pesoInformado: string;
  alturaInformada: string;
  larguraInformada: string;
  comprimentoInformado: string;
  statusAtual: number;
  dataHoraStatusAtual: string;
  descStatusAtual: string;
  codigoFormatoObjetoInformado: string;
  numeroCartaoPostagem: string;
  dataHora: string;
  cienteObjetoNaoProibido: number;
  tipoRotulo: string;
  modalidadePagamento: number;
  idCorreios: string;
  sistemaOrigem: string;
  tipoObjeto: string;
  historicoStatus: any[];
  logisticaReversa: string;
  prazoPostagem: string;
}

interface PrepostResponse {
  itens: PrepostItem[];
  page: {
    size: number;
    numberElements: number;
    totalPages: number;
    number: number;
    next: boolean;
    previous: boolean;
    first: boolean;
    last: boolean;
  };
}

interface Summary {
  quantity: number;
  valueTotal: number;
}

type RefundStatus =
  | 'ABERTO'
  | 'EM_CHAT'
  | 'ENVIADO_PLATAFORMA'
  | 'APROVADO'
  | 'REPROVADO'
  | 'REEMBOLSADO'
  | 'CANCELADO';

interface RefundSolicitation {
  id: string;
  orderId: string;
  repaymentUser: Client | null;
  userId: string;
  userName: string;
  sellerId: string;
  createdAt: string;
  status: RefundStatus;
  motive: string;
  description: string;
  images: string[];
}

type ApiRepaymentRow = {
  images: any;
  id: string;
  repaymentSolicitationDate: string;
  repaymentMotive: string;
  order: {
    id: string;
    clientId: string;
    client: string;
    code: string;
    orderDate: string;
    paymentStatus: string;
    amount: number;
    orderStatus: string;
    totalValue: number;
    reverseValue: number;
    sendEmail: boolean;
    sendPush: boolean;
    repaymentSolicitationDate: string;
    repaymentReason: string;
    repaymentUser: {
      id: string;
      idPagarme: string | null;
      name: string;
      type: string;
      cpf: string;
      email: string;
      gender: string;
      birthdate: string;
      cellPhone: string;
      password: string;
      sellerId: string | null;
      idSellerPagarme: string | null;
      address: unknown;
      image: string | null;
      cards: unknown;
      cart: unknown;
    };
    repaymentMotive: string;
    iniPeriodRefund: string;
    endPeriodRefund: string;
  };
};

const OrderPreparation: React.FC = () => {
  const [orderItems, setOrderItems] = useState<OrderSummary[]>([]);
  const [order, setOrder] = useState<OrderSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [prepostDetails, setPrepostDetails] = useState<PrepostItem | null>(
    null
  );
  const [modalLoading, setModalLoading] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [bankData, setBankData] = useState({
    bank: '',
    branch_number: '',
    branch_check_digit: '',
    account_number: '',
    account_check_digit: '',
    type: 'corrente',
  });
  const [sendLoadingId, setSendLoadingId] = useState<string | null>(null);
  const [voucherAction, setVoucherAction] = useState<
    'confirm-payment' | 'cancel' | null
  >(null);
  const [refundsByOrder, setRefundsByOrder] = useState<
    Record<string, ApiRepaymentRow>
  >({});
  const [summary, setSummary] = useState<Summary>();
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState<ApiRepaymentRow | null>(
    null
  );

  const [confirmRepaymentOpen, setConfirmRepaymentOpen] = useState(false);
  const [repaymentSubmitting, setRepaymentSubmitting] = useState(false);

  const norm = (v?: string | null) =>
    String(v ?? '')
      .trim()
      .toLowerCase();

  const isPreparing = (s?: string) =>
    ['Preparando', 'PREPARING', 'AWAITING_ERP'].includes(String(s));

  const isVoucher = (o?: Pick<OrderSummary, 'paymentMethod'>) =>
    o?.paymentMethod === 'VOUCHER';

  const isVoucherPaid = (o?: Pick<OrderSummary, 'paymentStatus'>) =>
    ['APPROVED', 'PAID'].includes(String(o?.paymentStatus ?? ''));

  const isVoucherCanceled = (
    o?: Pick<OrderSummary, 'paymentStatus' | 'orderStatus'>
  ) =>
    ['CANCELED', 'CANCELLED'].includes(String(o?.paymentStatus ?? '')) ||
    ['CANCELED', 'CANCELLED'].includes(String(o?.orderStatus ?? ''));

  const voucherPaymentLabel = (o?: OrderSummary) => {
    if (isVoucherCanceled(o)) return 'Pagamento cancelado';
    if (isVoucherPaid(o)) return 'Pagamento confirmado';
    return 'Voucher · Pendente';
  };

  const selectedOrder: OrderSummary | undefined = selectedOrderId
    ? order.find((o) => o.id === selectedOrderId)
    : undefined;

  const isLocalDelivery = (o?: Pick<OrderSummary, 'correiosService'>) =>
    o?.correiosService === 'ENTREGA_LOCAL';

  const calcSummary = (orders: OrderSummary[]): void => {
    let newValueTotal: number = 0;
    for (let i = 0; orders.length > i; i++) {
      newValueTotal += orders[i].totalValue;
    }
    const newSummary: Summary = {
      quantity: orders.length,
      valueTotal: newValueTotal,
    };
    setSummary(newSummary);
  };

  const fetchOrderItems = async () => {
    try {
      setLoading(true);
      const { data } = await api.get<OrderSummary[]>('/order/allbyseller');
      const voucherResponses = await Promise.allSettled([
        api.get<VoucherOrderSummary[]>('/order/voucher/find-store'),
        api.get<VoucherOrderSummary[]>('/order/voucher/recover'),
      ]);
      const vouchers = voucherResponses.flatMap((response) =>
        response.status === 'fulfilled' ? response.value.data : []
      );
      const vouchersByCode = new Map(
        vouchers.map((voucher) => [norm(voucher.orderCode), voucher])
      );
      const enrichedOrders = data.map((currentOrder) => {
        if (!isVoucher(currentOrder)) return currentOrder;
        const voucher = vouchersByCode.get(norm(currentOrder.code));
        if (!voucher) return currentOrder;
        return {
          ...currentOrder,
          voucherOrderId: voucher.orderId,
          voucherStatus: voucher.voucherStatus,
          paymentStatus: voucher.paymentStatus ?? currentOrder.paymentStatus,
          orderStatus: voucher.orderStatus ?? currentOrder.orderStatus,
        };
      });
      calcSummary(enrichedOrders);
      setOrder(enrichedOrders);
      // const normalized: OrderSummary[] = data.map((o: any) => ({
      //   ...o,
      //   pagarmeStatus: o.pagarmestatus ?? undefined,
      // }));

      // const sorted = normalized.sort((a, b) => {
      //   const dateA = new Date(a.orderDate || 0).getTime();
      //   const dateB = new Date(b.orderDate || 0).getTime();
      //   return dateB - dateA;
      // });
    } catch (err) {
      console.error('Erro ao buscar itens do pedido:', err);
      toast.error('Algo deu errado');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderItems();
  }, []);

  useEffect(() => {
    const fetchRefunds = async () => {
      try {
        const { data } = await api.get<ApiRepaymentRow[]>(
          '/order/repayments-solicitations'
        );

        const map: Record<string, ApiRepaymentRow> = {};
        (Array.isArray(data) ? data : []).forEach((row) => {
          const code = (row as any)?.order?.code;
          if (code) map[norm(code)] = row;

          const sellers = (row as any)?.sellers;
          if (Array.isArray(sellers)) {
            sellers.forEach((s) => {
              const sellerCode = s?.orderSeller?.code;
              if (sellerCode) map[norm(sellerCode)] = row;
            });
          }
        });

        console.log('[refunds] keys:', Object.keys(map));
        setRefundsByOrder(map);
      } catch (e) {
        console.error('refunds fetch error', e);
        setRefundsByOrder({});
      }
    };
    fetchRefunds();
  }, []);

  const hasMultipleSellers = (o: OrderSummary | undefined) => {
    return o!.multiSellers === true;
  };

  const mustAskBankForRefund = (o: OrderSummary | undefined) =>
    !!o && o.paymentMethod === 'BOLETO' && o.pagarmeStatus === 'paid';

  const handleSendLocal = async (orderId: string): Promise<void> => {
    if (!orderId) return;
    setSendLoadingId(orderId);
    const payload = {
      orders: [
        {
          id: orderId,
          locator: '',
        },
      ],
    };
    try {
      await api.post('/order/send', payload);
      toast.success('Pedido marcado como "A Caminho".');

      setOrderItems((prev) =>
        prev.map((o) =>
          o.id === orderId ? { ...o, orderStatus: 'ON_THE_WAY' } : o
        )
      );
      setOrder((prev) =>
        prev.map((o) =>
          o.id === orderId ? { ...o, orderStatus: 'ON_THE_WAY' } : o
        )
      );
      setOrderDetails((prev) =>
        prev ? { ...prev, status: 'ON_THE_WAY' } : prev
      );
    } catch {
      toast.error('Falha ao marcar como enviado.');
    } finally {
      setSendLoadingId(null);
    }
  };

  const handleMarkCOMPLETED = async (orderId: string): Promise<void> => {
    if (!orderId) return;
    setSendLoadingId(orderId);
    const payload = {
      orders: [
        {
          id: orderId,
        },
      ],
    };
    try {
      await api.post('/order/sent-order', payload);
      toast.success('Pedido marcado como "Entregue".');

      setOrderItems((prev) =>
        prev.map((o) =>
          o.id === orderId ? { ...o, orderStatus: 'COMPLETED' } : o
        )
      );
      setOrder((prev) =>
        prev.map((o) =>
          o.id === orderId ? { ...o, orderStatus: 'COMPLETED' } : o
        )
      );
      setOrderDetails((prev) =>
        prev ? { ...prev, status: 'COMPLETED' } : prev
      );
    } catch {
      toast.error('Falha ao marcar como entregue.');
    } finally {
      setSendLoadingId(null);
    }
  };

  const findRefundForOrder = (o: OrderSummary) => {
    const byCode = refundsByOrder[norm((o as any).code)];
    if (byCode) return byCode;

    return undefined;
  };

  const openRefundDetails = (orderId: string) => {
    const r = refundsByOrder[orderId];
    if (!r) return;
    setSelectedRefund(r);
    setRefundModalOpen(true);
  };

  const openChatWithBuyer = async (refund: RefundSolicitation) => {
    console.log(refund);
    try {
      await api.post('/chat', refund.userId);
      toast.success('Chat aberto.');
    } catch (e) {
      toast.error('Falha ao abrir chat.');
    }
  };

  const sendRepaymentToPlatform = async (orderId: string) => {
    try {
      setRepaymentSubmitting(true);
      await api.post('/order/repayment-order', { id: orderId });
      toast.success(
        'sua solicitação foi enviada com sucesso. Se for necessário entraremos em contato.'
      );
      setConfirmRepaymentOpen(false);
      setRefundModalOpen(false);
    } catch {
      toast.error('Falha ao enviar solicitação de reembolso à plataforma.');
    } finally {
      setRepaymentSubmitting(false);
    }
  };

  const fetchOrderDetails = async (orderId: string) => {
    try {
      setModalLoading(true);
      setSelectedOrderId(orderId);
      const { data } = await api.get<OrderDetails>(
        `order/seller-order-item/${orderId}`
      );
      setOrderDetails(data);
      try {
        const prepostRes = await api.get<PrepostResponse>(
          `/seller/prepost/${orderId}`
        );
        const firstPrepostItem = prepostRes.data.itens?.[0] || null;
        setPrepostDetails(firstPrepostItem);
      } catch (prepostErr) {
        console.error('Erro ao buscar detalhes da pré-postagem:', prepostErr);
        setPrepostDetails(null);
      }
    } catch (err) {
      console.error('Erro ao buscar detalhes do pedido:', err);
      setOrderDetails(null);
      setPrepostDetails(null);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedOrderId(null);
    setOrderDetails(null);
    setPrepostDetails(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Aguardando':
        return 'bg-red-50 text-red-600 border border-red-200';
      case 'Preparando':
        return 'bg-red-100 text-red-700 border border-red-300';
      case 'Enviado':
        return 'bg-red-600 text-white border border-red-600';
      case 'Cancelado':
        return 'bg-gray-100 text-gray-600 border border-gray-300';
      default:
        return 'bg-red-50 text-red-600 border border-red-200';
    }
  };

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case 'AWAITING_ERP':
        return 'bg-amber-50 text-amber-700 border border-amber-200';
      case 'PENDING_PAYMENT':
        return 'bg-red-50 text-red-600 border border-red-200';
      case 'PREPARING':
        return 'bg-red-100 text-red-700 border border-red-300';
      case 'PAID':
        return 'bg-red-600 text-white border border-red-600';
      case 'ON_THE_WAY':
        return 'bg-red-100 text-red-700 border border-red-300';
      case 'COMPLETED':
        return 'bg-red-500 text-white border border-red-500';
      case 'CANCELLED':
      case 'CANCELED':
        return 'bg-gray-100 text-gray-600 border border-gray-300';
      default:
        return 'bg-gray-100 text-gray-600 border border-gray-300';
    }
  };

  const getOrderStatusLabel = (status: string) => {
    switch (status) {
      case 'AWAITING_ERP':
        return 'Aguardando aceite';
      case 'PENDING_PAYMENT':
        return 'Pagamento Pendente';
      case 'PREPARING':
        return 'Preparando';
      case 'PAID':
        return 'Pago';
      case 'ON_THE_WAY':
        return 'Enviado';
      case 'COMPLETED':
        return 'Entregue';
      case 'CANCELLED':
      case 'CANCELED':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const formatPrice = (value: number | undefined | null) => {
    if (typeof value !== 'number' || isNaN(value)) return 'R$ 0,00';
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // const getProductImage = (order: OrderSummary): string => {
  //   let image: string | undefined;

  //   for (let i = 0; i < order.orderItems.length; i++) {
  //     const grade = order.orderItems[i].grade;
  //     if (grade && grade.image) {
  //       image = identifyApi + grade.image;
  //       break;
  //     }
  //   }

  //   return image ?? '/placeholder.svg?height=80&width=80';
  // };

  const getProductGrade = (item: ProductOrderItem) => {
    const grade = item?.productDTO?.grade?.[0];
    const gradeSize = grade?.gradeSizes?.[0];
    if (!grade || !gradeSize) return null;
    return {
      color: grade.color || 'N/A',
      size: gradeSize.size || 'N/A',
      stock: gradeSize.stock?.toString() || '0',
    };
  };

  const openCancel = () => {
    const currentOrder = order.find((o) => o.id === selectedOrderId);
    if (!currentOrder) return;

    if (hasMultipleSellers(currentOrder)) {
      toast.warning(
        'Pedido com múltiplos sellers (split). Cancelamento pelo seller não é permitido.'
      );
      return;
    }

    if (mustAskBankForRefund(currentOrder)) {
      setIsBankModalOpen(true);
    } else {
      setIsCancelOpen(true);
    }
  };

  const closeCancel = () => setIsCancelOpen(false);
  const closeBankModal = () => {
    setIsBankModalOpen(false);
    setBankData({
      bank: '',
      branch_number: '',
      branch_check_digit: '',
      account_number: '',
      account_check_digit: '',
      type: 'corrente',
    });
  };

  const handleCancel = async () => {
    if (!selectedOrderId) return;

    try {
      setCancelLoading(true);

      const currentOrder = order.find((o) => o.id === selectedOrderId);
      if (!currentOrder) return;

      if (hasMultipleSellers(currentOrder)) {
        toast.warning(
          'Cancelamento bloqueado: pedido com múltiplos sellers (split).'
        );
        return;
      }

      if (isVoucher(currentOrder)) {
        if (!currentOrder.voucherOrderId) {
          toast.error(
            'Não foi possível identificar o Voucher. Atualize a página e tente novamente.'
          );
          return;
        }
        setVoucherAction('cancel');
        await api.post('/order/voucher/cancel', {
          orderId: currentOrder.voucherOrderId,
        });
        setOrder((prev) =>
          prev.map((o) =>
            o.id === selectedOrderId
              ? {
                  ...o,
                  orderStatus: 'CANCELED',
                  paymentStatus: 'CANCELED',
                }
              : o
          )
        );
        setOrderDetails((prev) =>
          prev ? { ...prev, status: 'CANCELED' } : prev
        );
        setIsCancelOpen(false);
        toast.success('Pedido Voucher cancelado.');
        return;
      }

      const payload = {
        orders: [
          {
            id: selectedOrderId,
            paymentMethod: currentOrder.paymentMethod || '',
            ...(mustAskBankForRefund(currentOrder) && {
              bank: {
                bank: bankData.bank,
                branch_number: bankData.branch_number,
                branch_check_digit: bankData.branch_check_digit,
                account_number: bankData.account_number,
                account_check_digit: bankData.account_check_digit,
                type: bankData.type,
              },
            }),
          },
        ],
      };

      const response = await api.post(`/order/close-seller`, payload);

      if (response.status === 422) {
        toast.error(
          response.data?.mensagem || 'Não é possível cancelar este pedido.'
        );
        return;
      }

      setOrder((prev) =>
        prev.map((o) =>
          o.id === selectedOrderId ? { ...o, orderStatus: 'CANCELLED' } : o
        )
      );
      setOrderDetails((prev) =>
        prev ? { ...prev, status: 'CANCELLED' } : prev
      );
      setIsCancelOpen(false);
      setIsBankModalOpen(false);
    } catch (err: any) {
      const message =
        err.response?.data?.message ?? err.response?.data?.mensagem;
      if (message) {
        toast.error(message);
        return;
      }
      if (err.response?.status === 422) {
        toast.error(err.response.data?.mensagem);
        return;
      }

      // console.error('Erro ao cancelar pedido:', err);
      //toast.error('Erro ao cancelar pedido.');
    } finally {
      setCancelLoading(false);
      setVoucherAction(null);
    }
  };

  const handleConfirmVoucherPayment = async () => {
    if (!selectedOrderId || !selectedOrder || !isVoucher(selectedOrder)) return;

    if (!selectedOrder.voucherOrderId) {
      toast.error(
        'Não foi possível identificar o Voucher. Atualize a página e tente novamente.'
      );
      return;
    }

    try {
      setVoucherAction('confirm-payment');
      const { data } = await api.post('/order/voucher/confirm-payment', {
        orderId: selectedOrder.voucherOrderId,
      });
      setOrder((prev) =>
        prev.map((o) =>
          o.id === selectedOrderId
            ? {
                ...o,
                paymentStatus: data?.paymentStatus ?? 'APPROVED',
                orderStatus: data?.orderStatus ?? o.orderStatus,
              }
            : o
        )
      );
      setOrderDetails((prev) =>
        prev && data?.orderStatus ? { ...prev, status: data.orderStatus } : prev
      );
      toast.success(
        data?.repeated
          ? 'O pagamento já estava confirmado.'
          : 'Pagamento Voucher confirmado.'
      );
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ??
          err.response?.data?.mensagem ??
          'Não foi possível confirmar o pagamento.'
      );
    } finally {
      setVoucherAction(null);
    }
  };

  const handleBankCancel = async () => {
    const currentOrder = order.find((o) => o.id === selectedOrderId);

    if (!mustAskBankForRefund(currentOrder)) {
      await handleCancel();
      return;
    }

    if (!bankData.bank || !bankData.branch_number || !bankData.account_number) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    await handleCancel();
  };

  const totalValue = order.reduce((sum, order) => sum + order.totalValue, 0);

  const getDeclarationContent = async (ids: string) => {
    try {
      const { data } = await api.get(
        `/seller/prepost/declaration/content/${ids}`
      );
      console.log('Declaration Content:', data);
      return data;
    } catch (error) {
      console.error('Error fetching declaration content:', error);
      return null;
    }
  };

  const deletePrepost = async (id: string) => {
    try {
      await api.delete(`/seller/prepost/delete/${id}`);
      console.log(`Prepost ${id} deleted successfully.`);
    } catch (error) {
      console.error('Error deleting prepost:', error);
    }
  };

  const getReceivingMessage = async (ids: string) => {
    try {
      const { data } = await api.get(
        `/seller/prepost/receiving/message/${ids}`
      );
      console.log('Receiving Message:', data);
      return data;
    } catch (error) {
      console.error('Error fetching receiving message:', error);
      return null;
    }
  };

  const regeneratePrepost = async (id: string) => {
    try {
      const { data } = await api.post(`/seller/prepost/regenerate/${id}`);
      console.log(`Prepost ${id} regenerated:`, data);
      return data;
    } catch (error) {
      console.error('Error regenerating prepost:', error);
      return null;
    }
  };

  const openBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const generateLabelPdf = async (id: string) => {
    try {
      const { data } = await api.get(`/seller/prepost/label/${id}/download`, {
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
        transformResponse: [],
      });

      openBlob(data, `etiqueta-${id}.pdf`);
    } catch (error) {
      console.error('Erro ao gerar ou baixar a etiqueta PDF:', error);
    }
  };

  const getPrepostPdf = async (id: string) => {
    try {
      const { data } = await api.get(`/seller/prepost/${id}/pdf`, {
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
      });

      openBlob(data, `prepost-${id}.pdf`);
    } catch (error) {
      console.error('Error generating prepost PDF:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-red-200 border-t-red-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Carregando vendas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-5 md:py-8 max-sm:overflow-x-hidden">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">
            Dashboard de Vendas
          </h1>
          <p className="text-sm md:text-base text-gray-600">
            Gerencie seus pedidos
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2.5 md:gap-5 mb-5 md:mb-8">
          <div className="bg-white rounded-lg p-3 sm:p-4 lg:p-5 border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center min-w-0">
              <div className="w-9 h-9 sm:w-10 sm:h-10 lg:w-11 lg:h-11 bg-red-100 rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                <svg
                  className="w-5 h-5 md:w-6 md:h-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4-8-4m16 0v10l-8 4-8-4V7"
                  />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-xs md:text-sm text-gray-500 mb-0.5 md:mb-1">
                  Total de Pedidos
                </p>
                <p className="text-xl md:text-2xl font-bold text-gray-900">
                  {summary ? summary.quantity : 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 sm:p-4 lg:p-5 border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center min-w-0">
              <div className="w-9 h-9 sm:w-10 sm:h-10 lg:w-11 lg:h-11 bg-red-100 rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                <svg
                  className="w-5 h-5 md:w-6 md:h-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                  />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-xs md:text-sm text-gray-500 mb-0.5 md:mb-1">
                  Receita Total
                </p>
                <p className="text-sm sm:text-base lg:text-xl xl:text-2xl font-bold text-gray-900 leading-tight tracking-tight break-words">
                  {summary ? formatPrice(summary.valueTotal) : 'R$ 0,00'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {order.map((order) => {
            return (
              <div
                key={order.id}
                className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="p-3.5 sm:p-4 md:p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4 md:mb-6 gap-3 lg:gap-6">
                    <div className="flex items-start gap-3 md:gap-4 flex-1">
                      <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                        <img
                          src={
                            `${process.env.REACT_APP_BASE_URL}/uploads/` +
                            order.orderItems[0].grade.image
                          }
                          className="w-full h-full object-cover"
                          alt=""
                        />
                      </div>

                      <div className="flex-1 min-w-0 break-words">
                        <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1">
                          Cliente {order.client}
                        </h3>

                        <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs md:text-sm text-gray-500 mb-1.5 md:mb-2">
                          <span>
                            Data da compra:{' '}
                            {dayjs(order.orderDate).format('DD/MM/YYYY HH:mm')}
                          </span>
                          <span
                            className={`px-2 py-0.5 md:px-3 md:py-1 rounded text-xs md:text-sm font-medium ${getOrderStatusColor(order.orderStatus)}`}
                          >
                            {getOrderStatusLabel(order.orderStatus)}
                          </span>
                          {isVoucher(order) && (
                            <span
                              className={`px-2 py-0.5 rounded text-[11px] sm:text-xs font-medium border ${
                                isVoucherPaid(order)
                                  ? 'bg-green-50 text-green-700 border-green-200'
                                  : isVoucherCanceled(order)
                                    ? 'bg-gray-100 text-gray-600 border-gray-300'
                                    : 'bg-amber-50 text-amber-700 border-amber-200'
                              }`}
                            >
                              {voucherPaymentLabel(order)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-end justify-between lg:block text-left lg:text-right lg:flex-shrink-0 lg:ml-6 pl-16 lg:pl-0">
                      <div className="text-lg md:text-xl font-bold text-gray-900 mb-0.5 md:mb-1">
                        {formatPrice(order.totalValue)}
                      </div>
                      <div className="text-xs md:text-sm text-gray-500">
                        Quantidade: {order.orderItems.length}
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-3 md:pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          className="px-3 md:px-4 py-1.5 md:py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                          onClick={() => fetchOrderDetails(order.id)}
                        >
                          Ver Detalhes
                        </button>
                        {refundsByOrder[norm((order as any).code)] && (
                          <button
                            className="px-3 md:px-4 py-1.5 md:py-2 text-sm font-medium rounded-lg bg-red-100 text-red-800 border border-red-300 hover:bg-red-200 transition-colors"
                            onClick={() => {
                              const refund =
                                refundsByOrder[norm((order as any).code)];
                              if (!refund) return;
                              setSelectedRefund(refund);
                              setRefundModalOpen(true);
                            }}
                            title="Ver solicitação de reembolso"
                          >
                            Reembolso
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {order.length === 0 && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4-8-4m16 0v10l-8 4-8-4V7"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Nenhuma venda encontrada
              </h3>
              <p className="text-gray-500">
                Suas vendas aparecerão aqui quando os pedidos chegarem.
              </p>
            </div>
          </div>
        )}
      </div>
      {selectedOrderId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch sm:items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[96vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  Detalhes do Pedido
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              {modalLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-3 border-red-200 border-t-red-500 rounded-full animate-spin"></div>
                </div>
              ) : orderDetails ? (
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <span
                      className={`px-3 py-1 rounded text-sm font-medium ${getOrderStatusColor(orderDetails.status)}`}
                    >
                      {getOrderStatusLabel(orderDetails.status)}
                    </span>
                    <div className="text-sm text-gray-500">
                      <h3 className="font-semibold text-gray-900">
                        Pedido feito em:
                      </h3>
                      {formatDate(orderDetails.orderDate)}
                    </div>
                  </div>
                  {isVoucher(selectedOrder) && (
                    <div
                      className={`rounded-lg border px-4 py-3 ${
                        isVoucherPaid(selectedOrder)
                          ? 'bg-green-50 border-green-200 text-green-800'
                          : isVoucherCanceled(selectedOrder)
                            ? 'bg-gray-50 border-gray-200 text-gray-700'
                            : 'bg-amber-50 border-amber-200 text-amber-800'
                      }`}
                    >
                      <div className="text-sm font-semibold">
                        Pagamento Voucher
                      </div>
                      <div className="text-sm mt-0.5">
                        {voucherPaymentLabel(selectedOrder)}
                      </div>
                    </div>
                  )}
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="font-semibold text-gray-900 mb-4">
                      Produto(s)
                    </h3>
                    {orderDetails.orderItens.map((item, index) => {
                      const grade = getProductGrade(item);
                      return (
                        <div
                          key={item.id}
                          className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-4 last:mb-0 overflow-hidden"
                        >
                          <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                            {item.grade && (
                              <img
                                src={
                                  item.grade.image
                                    ? `${process.env.REACT_APP_BASE_URL}/uploads/` +
                                      item.grade.image
                                    : '/placeholder.svg'
                                }
                                alt={item.itemName}
                                className="w-16 h-16 rounded-lg object-cover bg-white"
                              />
                            )}
                            <div className="flex-1 min-w-0 w-full">
                              <h4 className="font-medium text-gray-900 mb-1 break-words">
                                {item.itemName}
                              </h4>
                              <p className="text-sm text-gray-600 mb-3 break-words">
                                {item.productDTO?.description}
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-sm">
                                <div>
                                  <span className="text-gray-500">
                                    Quantidade:
                                  </span>
                                  <span className="ml-1 font-medium">
                                    {item.quantity}
                                  </span>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-sm">
                                <div>
                                  <span className="text-gray-500">SKU:</span>
                                  <span className="ml-1 font-medium">
                                    {item.productDTO?.sku}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Marca:</span>
                                  <span className="ml-1 font-medium">
                                    {item.productDTO?.brand?.name}
                                  </span>
                                </div>
                              </div>
                              {item.grade && (
                                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-2">
                                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                                    {item.grade.color}
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    Tam: {item.gradeSize.size}
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    Est: {item.gradeSize.stock}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="border-t border-gray-200 mt-4 pt-4 flex justify-between items-center">
                            <div>
                              <span className="text-sm text-gray-500"></span>
                              <div className="font-semibold"></div>
                            </div>
                            <div className="text-right">
                              <span className="text-sm text-gray-500">
                                Valor unitário
                              </span>
                              <div className="text-lg font-bold text-red-600">
                                {formatPrice(item.unitValue)}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div className="flex flex-col sm:block sm:text-right mt-4">
                      <span className="text-lg font-semibold text-gray-900">
                        Total desta loja:{' '}
                      </span>
                      <span className="text-2xl font-bold text-red-700">
                        {formatPrice(orderDetails.totalValue ?? 0)}
                      </span>
                    </div>

                    {orderDetails &&
                      !isVoucher(selectedOrder) &&
                      ['PENDING_PAYMENT'].includes(orderDetails.status) && (
                        <div className="pt-6">
                          <button
                            onClick={openCancel}
                            className="w-full h-11 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                          >
                            Cancelar Pedido
                          </button>
                        </div>
                      )}

                    {isVoucher(selectedOrder) &&
                      !isVoucherPaid(selectedOrder) &&
                      !isVoucherCanceled(selectedOrder) && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
                          {selectedOrder?.orderStatus !== 'COMPLETED' && (
                            <button
                              type="button"
                              onClick={openCancel}
                              disabled={voucherAction !== null}
                              className="w-full h-11 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-60 transition-colors"
                            >
                              Cancelar pedido
                            </button>
                          )}
                          {selectedOrder?.orderStatus !== 'AWAITING_ERP' && (
                            <button
                              type="button"
                              onClick={handleConfirmVoucherPayment}
                              disabled={voucherAction !== null}
                              className="w-full h-11 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60 transition-colors"
                            >
                              {voucherAction === 'confirm-payment'
                                ? 'Confirmando...'
                                : 'Confirmar pagamento'}
                            </button>
                          )}
                        </div>
                      )}

                    {isPreparing(selectedOrder?.orderStatus) &&
                      isLocalDelivery(selectedOrder) && (
                        <Button
                          title="Enviar Pedido"
                          size="sm"
                          onClick={() => handleSendLocal(selectedOrder!.id)}
                          disabled={sendLoadingId === selectedOrder?.id}
                          className="w-full bg-green-600 hover:bg-green-700 text-white h-9 mt-2"
                        >
                          {sendLoadingId === selectedOrder?.id ? (
                            <>
                              <Truck className="w-4 h-4 mr-2 animate-pulse" />
                              Enviando...
                            </>
                          ) : (
                            <>
                              <Truck className="w-4 h-4 mr-2" />
                              Enviar Pedido
                            </>
                          )}
                        </Button>
                      )}

                    {(selectedOrder?.orderStatus === 'ON_THE_WAY' ||
                      orderDetails?.status === 'ON_THE_WAY') && (
                      <Button
                        title="Marcar como Entregue"
                        size="sm"
                        onClick={() => handleMarkCOMPLETED(selectedOrder!.id)}
                        disabled={sendLoadingId === selectedOrder?.id}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white h-9 mt-2"
                      >
                        {sendLoadingId === selectedOrder?.id ? (
                          <>
                            <Truck className="w-4 h-4 mr-2 animate-pulse" />
                            Marcando...
                          </>
                        ) : (
                          <>
                            <Truck className="w-4 h-4 mr-2" />
                            Marcar como Entregue
                          </>
                        )}
                      </Button>
                    )}

                    {isCancelOpen && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
                        <div className="bg-white rounded-lg w-full max-w-md">
                          <div className="p-6 border-b border-gray-200">
                            <h3 className="text-lg font-bold text-gray-900 text-center">
                              Confirmar Cancelamento
                            </h3>
                            <p className="mt-2 text-sm text-gray-600 text-center">
                              Confirmar o cancelamento deste pedido? Esta ação
                              não pode ser desfeita.
                            </p>
                          </div>
                          <div className="p-6 space-y-3">
                            <button
                              onClick={handleCancel}
                              disabled={cancelLoading}
                              className="w-full h-11 rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-70"
                            >
                              {cancelLoading
                                ? 'Cancelando...'
                                : 'Sim, cancelar pedido'}
                            </button>
                            <button
                              onClick={closeCancel}
                              disabled={cancelLoading}
                              className="w-full h-11 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-70"
                            >
                              Não, manter pedido
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="font-semibold text-gray-900 mb-4">
                      Cliente
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {orderDetails.client.username}
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center">
                          <svg
                            className="w-4 h-4 mr-2 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                          <span>{orderDetails.client.email}</span>
                        </div>
                        <div className="flex items-center">
                          <svg
                            className="w-4 h-4 mr-2 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                            />
                          </svg>
                          <span>{orderDetails.client.cellPhone}</span>
                        </div>
                      </div>
                      {orderDetails.client.address && (
                        <div className="pt-3 border-t border-gray-200">
                          <div className="flex items-start">
                            <svg
                              className="w-4 h-4 mr-2 text-gray-400 mt-0.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                            <div className="text-sm">
                              <div>
                                {orderDetails.client.address.street},{' '}
                                {orderDetails.client.address.number}
                              </div>
                              {orderDetails.client.address.complement && (
                                <div>
                                  {orderDetails.client.address.complement}
                                </div>
                              )}
                              <div>
                                {orderDetails.client.address.district} -{' '}
                                {orderDetails.client.address.city}/
                                {orderDetails.client.address.state}
                              </div>
                              <div>
                                CEP: {orderDetails.client.address.zipcode}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="font-semibold text-gray-900 mb-4">
                      Logística
                    </h3>
                    <div className="flex flex-col sm:flex-row sm:justify-end gap-2 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => generateLabelPdf(selectedOrderId!)}
                        className="w-full sm:w-auto px-3 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-200 rounded-lg hover:bg-red-200 transition-colors"
                      >
                        Gerar Etiqueta PDF
                      </button>
                      <button
                        onClick={() => getPrepostPdf(selectedOrderId!)}
                        className="w-full sm:w-auto px-3 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-200 rounded-lg hover:bg-red-200 transition-colors"
                      >
                        Gerar Declaração de Conteúdo PDF
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Erro ao carregar detalhes</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {isBankModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 text-center">
                Dados Bancários para Cancelamento
              </h3>
              <p className="mt-2 text-sm text-gray-600 text-center">
                Para cancelar pedidos pagos via boleto, precisamos dos dados
                bancários do seu cliente para estorno.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <AutocompleteBank
                  value={bankData.bank}
                  onChange={(val: string) =>
                    setBankData((prev) => ({
                      ...prev,
                      bank: val,
                    }))
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Agência *
                  </label>
                  <input
                    type="text"
                    value={bankData.branch_number}
                    onChange={(e) =>
                      setBankData((prev) => ({
                        ...prev,
                        branch_number: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dígito Agência
                  </label>
                  <input
                    type="text"
                    value={bankData.branch_check_digit}
                    onChange={(e) =>
                      setBankData((prev) => ({
                        ...prev,
                        branch_check_digit: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                    maxLength={1}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Conta *
                  </label>
                  <input
                    type="text"
                    value={bankData.account_number}
                    onChange={(e) =>
                      setBankData((prev) => ({
                        ...prev,
                        account_number: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="000000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dígito Conta *
                  </label>
                  <input
                    type="text"
                    value={bankData.account_check_digit}
                    onChange={(e) =>
                      setBankData((prev) => ({
                        ...prev,
                        account_check_digit: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                    maxLength={1}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Conta
                </label>
                <select
                  value={bankData.type}
                  onChange={(e) =>
                    setBankData((prev) => ({ ...prev, type: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="corrente">Conta Corrente</option>
                  <option value="poupanca">Conta Poupança</option>
                </select>
              </div>
              <div className="space-y-3 pt-4">
                <button
                  onClick={handleBankCancel}
                  disabled={cancelLoading}
                  className="w-full h-11 rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-70"
                >
                  {cancelLoading ? 'Cancelando...' : 'Confirmar Cancelamento'}
                </button>
                <button
                  onClick={closeBankModal}
                  disabled={cancelLoading}
                  className="w-full h-11 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-70"
                >
                  Voltar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {refundModalOpen && selectedRefund && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                Pedido de Reembolso
              </h3>
              <button
                onClick={() => setRefundModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Número do pedido</div>
                  <div className="font-medium">{selectedRefund.order.id}</div>
                </div>
                <div>
                  <div className="text-gray-500">Cliente</div>
                  <div className="font-medium">
                    {selectedRefund.order.client}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Motivo</div>
                  <div className="font-medium">
                    {selectedRefund.order.repaymentMotive}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Data</div>
                  <div className="font-medium">
                    {formatDate(selectedRefund.repaymentSolicitationDate)}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-gray-500">Descrição</div>
                  <div className="font-medium whitespace-pre-wrap">
                    {selectedRefund.order.repaymentReason}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  onClick={() =>
                    openChatWithBuyer({
                      orderId: selectedRefund.order.id,
                      userId: selectedRefund.order.repaymentUser.id,
                      userName: selectedRefund.order.repaymentUser.name,
                    } as any)
                  }
                  className="flex-1 h-10 rounded-md border border-red-300 text-red-800 hover:bg-red-50"
                >
                  Abrir chat
                </button>

                <button
                  onClick={() => {
                    setConfirmRepaymentOpen(true);
                  }}
                  className="flex-1 h-10 rounded-md bg-red-400 text-black hover:bg-red-500"
                >
                  Reembolsar
                </button>
              </div>
              {(selectedRefund?.images?.length ?? 0) > 0 && (
                <div>
                  <div className="text-gray-500 mb-2">Imagens enviadas</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {selectedRefund.images!.map(
                      (img: { imageUrl: string }, i: number) => (
                        <a
                          key={i}
                          href={`${process.env.REACT_APP_BASE_URL}/uploads/${img.imageUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block overflow-hidden rounded-md border border-gray-200 hover:opacity-90"
                        >
                          <img
                            src={`${process.env.REACT_APP_BASE_URL}/uploads/${img.imageUrl}`}
                            alt={`Imagem ${i + 1}`}
                            className="w-full h-40 object-cover"
                            loading="lazy"
                          />
                        </a>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmRepaymentOpen && selectedRefund && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[80] p-4">
          <div className="bg-white rounded-lg w-full max-w-md overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 text-center">
                Confirmar Reembolso
              </h3>
              <p className="mt-2 text-sm text-gray-600 text-center">
                Tem certeza que deseja enviar este pedido para reembolso?
              </p>
            </div>

            <div className="p-6 space-y-3">
              <button
                onClick={() => sendRepaymentToPlatform(selectedRefund.order.id)}
                disabled={repaymentSubmitting}
                className="w-full h-11 rounded-lg bg-red-500 hover:bg-red-600 text-black disabled:opacity-70"
              >
                {repaymentSubmitting ? 'Enviando...' : 'Sim, enviar reembolso'}
              </button>

              <button
                onClick={() => setConfirmRepaymentOpen(false)}
                disabled={repaymentSubmitting}
                className="w-full h-11 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-70"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderPreparation;
